/** Result from Electron `printers:check-pos-printer` IPC. */
export interface PosPrinterCheckResponse {
  available: boolean;
  printer: { name: string } | null;
  error: string | null;
  physicallyConnected?: boolean;
  physicalStatus?: string;
}

export interface PosPrinterGateResult {
  ready: boolean;
  message: string;
  printerName?: string;
}

export function getSlipPrinterFromPrefs(): string | undefined {
  try {
    const prefs = JSON.parse(localStorage.getItem('hria-prefs') || '{}');
    return prefs.slipPrinter || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Donations require the packaged Electron app with a physically connected POS printer.
 * Returns ready=false for browser, missing API, misconfiguration, or offline hardware.
 */
let cachedGate: { result: PosPrinterGateResult; at: number } | null = null;
const GATE_CACHE_MS = 5000;

export async function verifyPosPrinterReady(): Promise<PosPrinterGateResult> {
  if (cachedGate && Date.now() - cachedGate.at < GATE_CACHE_MS && cachedGate.result.ready) {
    return cachedGate.result;
  }

  if (!window.electronAPI?.checkPosPrinter) {
    return {
      ready: false,
      message:
        'Donations can only be recorded in the HRIA Desktop app with a connected POS printer. Open the installed desktop application (not the website in a browser).',
    };
  }

  const slipPrinter = getSlipPrinterFromPrefs();
  const result = (await window.electronAPI.checkPosPrinter(
    slipPrinter,
  )) as PosPrinterCheckResponse;

  const physicallyOk =
    result.physicallyConnected !== false && result.available === true;

  if (!physicallyOk) {
    const detail =
      result.error ||
      result.physicalStatus ||
      'POS printer is not physically connected';
    return { ready: false, message: detail };
  }

  const gateResult: PosPrinterGateResult = {
    ready: true,
    message: 'OK',
    printerName: result.printer?.name,
  };
  cachedGate = { result: gateResult, at: Date.now() };
  return gateResult;
}
